import { gql } from '@apollo/client';

const fragment = `
  fragment Data on Shipment {
      id
       sampleType {
        id
        code
        name
      }
      companyUuid,
      status {
        id
        name
        slug
        order
      }
      ingredient {
        id
        name
        code
      }
      origin {
        id
        name
        code
      }
      customer {
        id
        name
        code
        email
      }
      lab {
        id
        name
        code
      }

      duplicates

      shipmentId
      shipmentShippedDate
      shipmentInvoiceNumber
      shipmentInvoiceAmount
      shipmentTons
      shipmentCustomId
      shipmentRemarks
      sentShipment

      shipmentMoisturePercent
      shipmentFiberPercent
      shipmentProteinPercent

      claimId
      claimedDate
      claimPendingDenial
      claimDeniedDate
      claimMoisturePercent
      claimProtein12Percent
      claimFiber12Percent

      claimMoistureDollars
      claimFiberDollars
      claimProteinDollars

      refereeFee
      refereeToSend
      refereeSentDate
      refereeFile
      refereeMoisturePercent
      refereeProteinPercent
      refereeFiberPercent
      refereeProtein12Percent
      refereeFiber12Percent

      settlementMoisturePercent
      settlementFiber12Percent
      settlementProtein12Percent
      settlementProteinDollars
      settlementMoistureDollars
      settlementFiberDollars
      settlementAdjustmentDollars
      settlementTotalDollars
      settlementCheckNumber
      settlementSentDate
      settlementRemarks
  }
  `

const createShipmentInput = (data) => {
  return {
      id: data.id,
      sampleType: data.sampleType?.id ?? data.sampleType,
      ingredient: data.ingredient?.id ?? data.ingredient,
      customer: data.customer?.id ?? data.customer,
      origin: data.origin?.id ?? data.origin,
      lab: data.lab?.id ?? data.lab,

      shipmentId: data.shipmentId,
      shipmentShippedDate: data.shipmentShippedDate,
      shipmentInvoiceNumber: data.shipmentInvoiceNumber,
      shipmentInvoiceAmount: data.shipmentInvoiceAmount,
      shipmentTons: data.shipmentTons,
      shipmentCustomId: data.shipmentCustomId,
      shipmentRemarks: data.shipmentRemarks,
      sentShipment: data.sentShipment,

      shipmentMoisturePercent: data.shipmentMoisturePercent,
      shipmentProteinPercent: data.shipmentProteinPercent,
      shipmentFiberPercent: data.shipmentFiberPercent,

      claimCreated: true,
      claimId: data.claimId,
      claimedDate: data.claimedDate,
      claimDenied: data.claimDenied,
      claimPendingDenial: data.claimPendingDenial,
      claimMoisturePercent:  data.claimMoisturePercent,
      claimProtein12Percent: data.claimProtein12Percent,
      claimFiber12Percent: data.claimFiber12Percent,
      claimMoistureDollars: data.claimMoistureDollars,
      claimFiberDollars: data.claimFiberDollars,
      claimProteinDollars: data.claimProteinDollars,

      refereeToSend: data.refereeToSend,
      refereeSentDate: data.refereeSentDate,
      refereeFee: data.refereeFee,
      refereeFile: data.refereeFile,
      refereeMoisturePercent: data.refereeMoisturePercent,
      refereeProtein12Percent: data.refereeProtein12Percent,
      refereeFiberPercent: data.refereeFiberPercent,
      refereeFiber12Percent: data.refereeFiber12Percent,

      settlementAdjustmentDollars: data.settlementAdjustmentDollars,
      settlementCheckNumber: data.settlementCheckNumber,
      settlementSentDate: data.settlementSentDate,
      settlementRemarks: data.settlementRemarks,
  }
}

const GET_SHIPMENTS = gql`
  query shipments($status: Int, $sortBy: String, $cursor: Int, $sortOrder: String, $filter: String, $take: Int) {
    shipments(
      status: $status 
      cursor: $cursor
      filter: $filter 
      take: $take
      sortBy: $sortBy
      sortOrder: $sortOrder) {
      ...Data
    }
  }
  ${fragment}
`;

const GET_POWER_BI_REPORTS = gql`
  query powerBIReports {
    powerBIReports {
      id,
      embedURL,
      token,
      name
    }
  }
`;


const MUTATE_UPSERT_SHIPMENT = gql`
  mutation upsertShipment(
      $shipment: ShipmentInput
    ){
      upsertShipment(
        shipment: $shipment
      ) {
        ...Data
      }
    }
    ${fragment}
`;

const MUTATE_UPSERT_SHIPMENTS = gql`
  mutation upsertShipments(
      $shipment: [ShipmentInput]
      $status: Int
      $orderBy: String
      $filter: String
    ){
      upsertShipments(
        shipment: $shipment,
        status: $status,
        orderBy: $orderBy,
        filter: $filter,
      ) {
        ...Data
      }
    }
    ${fragment}
`;

const MUTATE_SEND_SUPPLIER = gql`
  mutation sendSupplier(
      $ids: [Int]
      $sendEmail: Boolean
      $query: ShipmentQueryInput
      $toAddress: String
      $message: String
      $replyTo: String
      # $ccAddress: String
      # $replyAddress: String
    ){
      sendSupplier(
        ids: $ids,
        sendEmail: $sendEmail
        query: $query,
        toAddress: $toAddress,
        message: $message,
        replyTo: $replyTo,
        # ccAddress: $ccAddress,
        # replyAddress: $replyAddress
      ) {
        ...Data
      }
    }
    ${fragment}
`;

const MUTATE_SEND_REFEREE = gql`
  mutation sendReferee(
      $ids: [Int]
      $lab: Int
      $sendEmail: Boolean
      $query: ShipmentQueryInput
      $customerToAddress: String
      $customerMessage: String
      $labToAddress: String
      $labMessage: String
      $replyTo: String
      # $ccAddress: String
      # $replyAddress: String
    ){
      sendReferee(
        ids: $ids,
        lab: $lab,
        sendEmail: $sendEmail
        query: $query,
        customerToAddress: $customerToAddress,
        customerMessage: $customerMessage,
        labToAddress: $labToAddress,
        labMessage: $labMessage,
        replyTo: $replyTo,
        # ccAddress: $ccAddress,
        # replyAddress: $replyAddress
      ) {
        ...Data
      }
    }
    ${fragment}
`;

const MUTATE_SEND_DENIED = gql`
  mutation sendDenied(
      $ids: [Int]
      $sendEmail: Boolean
      $query: ShipmentQueryInput
      $customerToAddress: String
      $customerMessage: String
      $replyTo: String
    ){
      sendDenied(
        ids: $ids,
        sendEmail: $sendEmail
        query: $query,
        customerToAddress: $customerToAddress,
        customerMessage: $customerMessage,
        replyTo: $replyTo,
      ) {
        ...Data
      }
    }
    ${fragment}
`;

const MUTATE_SEND_SETTLEMENT = gql`
  mutation sendSettlement(
      $ids: [Int]
      $checkNumber: String
      $sendEmail: Boolean
      $query: ShipmentQueryInput
      $toAddress: String
      $message: String
      $replyTo: String
      # $ccAddress: String
      # $replyAddress: String
    ){
      sendSettlement(
        ids: $ids,
        checkNumber: $checkNumber,
        sendEmail: $sendEmail
        query: $query,
        toAddress: $toAddress,
        message: $message,
        replyTo: $replyTo,
        # ccAddress: $ccAddress,
        # replyAddress: $replyAddress
      ) {
        ...Data
      }
    }
    ${fragment}
`;

const MUTATE_DELETE_SHIPMENTS = gql`
  mutation deleteShipments($ids: [Int] $query: ShipmentQueryInput) {
    deleteShipments(ids: $ids query:$query){
      ...Data
    }
  }
  ${fragment}
`;

const UPLOAD_REFEREE = gql`
  mutation uploadReferee($id: Int! $file: Upload! $query: ShipmentQueryInput) {
    uploadReferee(id: $id file: $file query:$query){
      ...Data
    }
  }
  ${fragment}
`;

const DOWNLOAD_REFEREE = gql`
  query refereeFileLink($id: Int!) {
    refereeFileLink(id: $id)
  }
`;

const DOWNLOAD_SETTLEMENT = gql`
  mutation downloadSettlement($ids: [Int] $checkNumber: String $status: Int) {
    downloadSettlement(ids: $ids checkNumber: $checkNumber status: $status)
  }
`;

const DOWNLOAD_EXCEL_REPORT = gql`
  mutation downloadExcelReport($startingDate: String) {
    downloadExcelReport(startingDate: $startingDate)
  }
`;

const DOWNLOAD_EXCEL_ALL_CLAIMS_REPORT = gql`
  mutation downloadExcelAllClaimsReport($startingDate: String $claimsOnly: Boolean) {
    downloadExcelAllClaimsReport(startingDate: $startingDate claimsOnly: $claimsOnly)
  }
`;

const DOWNLOAD_EXCEL_INVOICE_REQUIRED_REPORT = gql`
  mutation downloadExcelInvoiceRequiredReport {
    downloadExcelInvoiceRequiredReport
  }
`;

const IMPORT_DATA = gql`
  mutation importData($type: String! $file: Upload!) {
    importData(type: $type file: $file){
      ...Data
    }
  }
  ${fragment}
`;

export default {
  GET_SHIPMENTS,
  GET_POWER_BI_REPORTS,
  MUTATE_SEND_REFEREE,
  MUTATE_SEND_DENIED,
  MUTATE_SEND_SETTLEMENT,
  MUTATE_UPSERT_SHIPMENTS,
  MUTATE_UPSERT_SHIPMENT,
  UPLOAD_REFEREE,
  DOWNLOAD_REFEREE,
  DOWNLOAD_SETTLEMENT,
  DOWNLOAD_EXCEL_REPORT,
  DOWNLOAD_EXCEL_ALL_CLAIMS_REPORT,
  DOWNLOAD_EXCEL_INVOICE_REQUIRED_REPORT,
  createShipmentInput,
  MUTATE_DELETE_SHIPMENTS,
  MUTATE_SEND_SUPPLIER,
  IMPORT_DATA,
  fragment
}